import { useState } from 'react';
import {
  BookOpen,
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  DollarSign,
  Download,
  Eye,
  EyeOff,
  FileText,
  Link2,
  Lock,
  LucideProps,
  MapPin,
  Pencil,
} from 'lucide-react';
import SaveBar from './SaveBar';
import Toast from './Toast';

// Placeholder copy for sections with no backend yet (interim generation flow is a follow-on ticket).
// name / role (tagline) / location / LinkedIn / resume / availability / fees above are already
// sourced from the account (BPP-133/134); availability and fees are backend-stubbed pending real
// Salesforce fields (BPP-82) — see fetchProviderOnePagerMetadata in internal-backend.
const MOCK = {
  overview: [
    'This executive brings 20+ years of finance and operations leadership across PE-backed, middle-market companies, with a track record spanning carve-outs, post-acquisition integration, and finance function stand-ups.',
    'Most recently, they served as interim CFO for a $150M revenue industrial services platform, rebuilding the monthly close process, implementing a 13-week cash flow forecast, and supporting the sponsor through a successful add-on acquisition.',
  ],
  snapshot: [
    '20+ years of finance and operations leadership across PE-backed, middle-market platforms.',
    'Deep expertise standing up ERP frameworks, financial models, and KPI structures during periods of rapid growth or transition.',
    'Proven track record establishing credibility with lenders and sponsors within the first 30 days of an engagement.',
  ],
  why: [
    'Repeatedly selected by sponsors and boards to lead finance functions requiring rapid stabilization and reporting discipline during periods of significant change.',
    'Track record standing up finance functions in newly carved-out businesses, mapping directly to the immediate needs of this engagement.',
    'References consistently highlight the ability to build credibility with lenders and sponsors while stabilizing day-to-day operations.',
  ],
  introNotes: [
    'Vetting call completed; references confirmed with prior PE sponsors.',
    'Open to a 6–9 month term with extension; would consider fractional thereafter.',
    'Rate is negotiable for a guaranteed full-time mandate.',
  ],
};

type IconType = React.ComponentType<LucideProps>;

function Chip({ icon: Icon, href, children }: { icon: IconType; href?: string; children: React.ReactNode }) {
  const className =
    "inline-flex items-center gap-[6px] rounded-full border border-white/30 bg-white/[0.14] px-[11px] py-[5px] font-['Inter',sans-serif] text-[12.5px] font-medium text-white transition-colors hover:border-white hover:bg-white/[0.24]";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        <Icon size={13} />
        {children}
      </a>
    );
  }
  return (
    <span className={className}>
      <Icon size={13} />
      {children}
    </span>
  );
}

function CollapsibleSection({
  title,
  editable,
  onSaveEdit,
  children,
}: {
  title: React.ReactNode;
  editable?: boolean;
  onSaveEdit?: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);

  const toggleEdit = () => {
    if (editing) onSaveEdit?.();
    setEditing((e) => !e);
  };

  return (
    <section className="mt-[38px]">
      <div className="mb-[16px] flex items-center gap-[11px]">
        <button
          type="button"
          data-pdf-exclude
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Collapse section' : 'Expand section'}
          className="flex text-[#132543] transition-transform duration-150"
          style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
        >
          <ChevronDown size={16} />
        </button>
        <h3 className="font-['Hanken_Grotesk',sans-serif] text-[19px] font-bold text-[#132543]">{title}</h3>
        {editable && (
          <button
            type="button"
            data-pdf-exclude
            onClick={toggleEdit}
            className={
              "ml-auto flex items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] font-['Inter',sans-serif] text-[13.5px] font-semibold transition-colors " +
              (editing ? 'bg-[#6EBFA4]/10 text-[#6EBFA4]' : 'text-[#25ADE3] hover:bg-[#25ADE3]/10')
            }
          >
            {editing ? <Check size={14} /> : <Pencil size={14} />}
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      {open && (
        <div
          contentEditable={editing}
          suppressContentEditableWarning
          className={
            'rounded-[8px] outline-none transition-shadow ' +
            (editing ? '-mx-[12px] -my-[10px] bg-[#25ADE3]/5 px-[12px] py-[10px] shadow-[0_0_0_2px_rgba(75,171,224,0.45)]' : '')
          }
        >
          {children}
        </div>
      )}
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-[13px] pl-[2px]">
      {items.map((t, i) => (
        <li key={i} className="flex gap-[12px] text-[14.5px] leading-[1.55] text-[#29446D]">
          <span className="mt-[8px] h-[7px] w-[7px] shrink-0 rounded-full bg-[#6EBFA4]" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

type AvailabilityField = { value?: string; sub?: string };
type ProviderAvailability = { start?: AvailabilityField; onsite?: AvailabilityField; isPlaceholder?: boolean };
type ProviderFees = { value?: string; unit?: string; note?: string; isPlaceholder?: boolean };

function PlaceholderBadge() {
  return (
    <span
      data-pdf-exclude
      className="inline-flex items-center gap-[5px] rounded-full bg-amber-100 px-[9px] py-[3px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.03em] text-amber-800"
      title="Stubbed value — not yet backed by real Salesforce data. Do not publish as-is."
    >
      Placeholder
    </span>
  );
}

export default function InterimOnePager({
  fullName,
  tagline,
  location,
  linkedInUrl,
  resumeUrl,
  availability,
  fees,
}: {
  fullName?: string;
  tagline?: string;
  location?: string;
  linkedInUrl?: string;
  resumeUrl?: string | null;
  availability?: ProviderAvailability;
  fees?: ProviderFees;
}) {
  const displayFullName = fullName?.trim() || 'Provider';
  const displayFirstName = displayFullName.split(' ')[0];
  const availabilityStart = availability?.start ?? { value: 'Available within 2 weeks', sub: 'Immediate start considered' };
  const availabilityOnsite = availability?.onsite ?? { value: '3–4 days / week on-site', sub: 'Travel-ready' };
  const engagementFees = fees ?? {
    value: '$XXX–$XXX',
    unit: '/ hour',
    note: 'Full-time interim engagement · final rate confirmed at engagement',
  };
  const availabilityIsPlaceholder = availability?.isPlaceholder ?? true;
  const feesIsPlaceholder = fees?.isPlaceholder ?? true;

  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [overviewEditing, setOverviewEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [introNotesVisible, setIntroNotesVisible] = useState(true);
  const [lastEdited, setLastEdited] = useState<{ date: string; time: string; by: string }>({
    date: '',
    time: '',
    by: '',
  });
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const markEdited = () => {
    const now = new Date();
    setLastEdited({
      date: now.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' }),
      time: now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase(),
      by: 'You',
    });
  };

  const handleSectionSaved = () => {
    setToastMsg('Changes saved');
    markEdited();
  };

  const toggleOverviewEdit = () => {
    if (overviewEditing) handleSectionSaved();
    setOverviewEditing((e) => !e);
  };

  const handleSave = () => {
    markEdited();
    setToastMsg('Draft saved');
  };

  const handleSendEmail = () => {
    setToastMsg('One-pager emailed to client');
  };

  const handleRegenerate = () => {
    if (regenerating) return;
    setRegenerating(true);
    setToastMsg('Regenerating narrative with AI…');
    setTimeout(() => {
      setRegenerating(false);
      markEdited();
      setToastMsg('Narrative regenerated');
    }, 1800);
  };

  const handlePublish = () => {
    setStatus('published');
    markEdited();
    setToastMsg('Published to client platform');
  };

  const handleDownload = () => {
    setToastMsg('Generating PDF…');
  };

  return (
    <div className="relative pb-[88px]">
      {/* Banner with status pill, download action, and identity */}
      <div
        className="relative h-[159px] w-full overflow-hidden rounded-[14px] border border-[rgba(19,37,67,0.12)]"
        style={{
          background:
            'radial-gradient(120% 180% at 88% -30%, rgba(83,190,232,.55), transparent 55%),' +
            'radial-gradient(120% 160% at 12% 130%, rgba(110,191,164,.35), transparent 60%),' +
            'linear-gradient(105deg, #0e1d36 0%, #163056 46%, #1f4b86 100%)',
        }}
      >
        <div className="absolute left-[18px] top-[18px]" data-pdf-exclude>
          <span
            className="inline-flex items-center gap-[7px] rounded-full border px-[12px] py-[5px] font-['Inter',sans-serif] text-[12px] font-semibold text-white"
            style={{ borderColor: 'rgba(255,255,255,.28)', background: 'rgba(255,255,255,.16)' }}
          >
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{ background: status === 'published' ? '#34d399' : '#FBBF2B' }}
            />
            {status === 'published' ? 'Published' : 'Draft'}
          </span>
        </div>

        <button
          type="button"
          data-pdf-exclude
          onClick={handleDownload}
          className="absolute right-[18px] top-[18px] flex items-center gap-[7px] rounded-[8px] bg-white/95 px-[14px] py-[8px] font-['Inter',sans-serif] text-[13px] font-semibold text-[#132543] shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-colors hover:bg-white"
        >
          <Download size={15} /> Download
        </button>

        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[11px] px-[32px] pb-[20px]">
          <div>
            <p className="font-['Hanken_Grotesk',sans-serif] text-[27px] font-extrabold leading-[1.1] text-white">
              {displayFullName}
            </p>
            {tagline && (
              <p className="mt-[5px] font-['Open_Sans',sans-serif] text-[14.5px] font-medium text-white">{tagline}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-[8px]">
            {location && <Chip icon={MapPin}>{location}</Chip>}
            {linkedInUrl && (
              <Chip icon={Link2} href={linkedInUrl}>
                LinkedIn
              </Chip>
            )}
            {resumeUrl && (
              <Chip icon={FileText} href={resumeUrl}>
                Résumé
              </Chip>
            )}
          </div>
        </div>
      </div>

      {/* Executive Overview — editable, but not collapsible */}
      <div className="mt-[40px] rounded-[12px] bg-[#EAF4FB] px-[26px] py-[22px]">
        <div className="mb-[12px] flex items-center gap-[10px]">
          <BookOpen size={19} className="text-[#4BABE0]" />
          <strong className="font-['Hanken_Grotesk',sans-serif] text-[17px] font-bold text-[#132543]">
            Executive Overview
          </strong>
          <button
            type="button"
            data-pdf-exclude
            onClick={toggleOverviewEdit}
            className={
              "ml-auto flex items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] font-['Inter',sans-serif] text-[13.5px] font-semibold transition-colors " +
              (overviewEditing ? 'bg-[#6EBFA4]/10 text-[#6EBFA4]' : 'text-[#25ADE3] hover:bg-[#25ADE3]/10')
            }
          >
            {overviewEditing ? <Check size={14} /> : <Pencil size={14} />}
            {overviewEditing ? 'Done' : 'Edit'}
          </button>
        </div>
        <div
          contentEditable={overviewEditing}
          suppressContentEditableWarning
          className={
            'space-y-[14px] rounded-[8px] font-[\'Open_Sans\',sans-serif] text-[15px] leading-[1.6] text-[#29446D] outline-none transition-shadow ' +
            (overviewEditing ? '-mx-[12px] -my-[10px] bg-white/60 px-[12px] py-[10px] shadow-[0_0_0_2px_rgba(75,171,224,0.45)]' : '')
          }
        >
          {MOCK.overview.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>

      <CollapsibleSection title="Snapshot — Why They're The Right Fit" editable onSaveEdit={handleSectionSaved}>
        <BulletList items={MOCK.snapshot} />
      </CollapsibleSection>

      <CollapsibleSection
        title={
          <>
            Why <em className="italic">we</em> recommend {displayFirstName}
          </>
        }
        editable
        onSaveEdit={handleSectionSaved}
      >
        <BulletList items={MOCK.why} />
      </CollapsibleSection>

      <CollapsibleSection title="Availability">
        <div
          className="overflow-hidden rounded-[12px] border"
          style={{
            borderColor: 'rgba(110,191,164,.45)',
            background: 'linear-gradient(180deg, rgba(176,233,214,.30), rgba(176,233,214,.10))',
          }}
        >
          <div
            className="flex items-center gap-[9px] px-[22px] py-[13px]"
            style={{ background: 'rgba(110,191,164,.22)', borderBottom: '1px solid rgba(110,191,164,.32)' }}
          >
            <Calendar size={17} style={{ color: '#2f7d5e' }} />
            <strong
              className="font-['Hanken_Grotesk',sans-serif] text-[15px] font-bold tracking-[0.01em]"
              style={{ color: '#1f6049' }}
            >
              AVAILABILITY
            </strong>
          </div>
          <div className="grid grid-cols-3">
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(110,191,164,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <Clock size={14} /> Start Date
                {availabilityIsPlaceholder && <PlaceholderBadge />}
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {availabilityStart.value}
              </div>
              <div className="mt-[3px] text-[12.5px] font-medium text-[#60667C]">{availabilityStart.sub}</div>
            </div>
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(110,191,164,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <Briefcase size={14} /> On-Site Commitment
                {availabilityIsPlaceholder && <PlaceholderBadge />}
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {availabilityOnsite.value}
              </div>
              <div className="mt-[3px] text-[12.5px] font-medium text-[#60667C]">{availabilityOnsite.sub}</div>
            </div>
            <div className="px-[22px] py-[18px]">
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <MapPin size={14} /> Location
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {location || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Engagement & Documentation">
        <div className={resumeUrl ? 'grid grid-cols-[1.1fr_1fr] gap-[18px]' : 'grid grid-cols-1'}>
          <div className="rounded-[12px] border border-[#E2E2E2] bg-white px-[24px] py-[22px]">
            <div className="flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] text-[#60667C]">
              <DollarSign size={14} className="text-[#132543]" /> Engagement Rate
              {feesIsPlaceholder && <PlaceholderBadge />}
            </div>
            <div className="mt-[8px] font-['Hanken_Grotesk',sans-serif] text-[30px] font-extrabold tracking-[-0.01em] text-[#132543]">
              {engagementFees.value} <span className="text-[17px] font-semibold text-[#60667C]">{engagementFees.unit}</span>
            </div>
            <div className="mt-[2px] text-[12.5px] text-[#60667C]">{engagementFees.note}</div>
          </div>
          {resumeUrl && (
            <div className="flex flex-col justify-center gap-[4px] rounded-[12px] border border-[#E2E2E2] bg-white px-[24px] py-[22px]">
              <div className="mb-[4px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] text-[#60667C]">
                Full Documentation
              </div>
              <a
                href={resumeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-[9px] rounded-full border-[1.5px] border-[#25ADE3] px-[18px] py-[10px] font-['Inter',sans-serif] text-[14px] font-bold text-[#25ADE3] transition-colors hover:border-[#132543] hover:bg-[#132543] hover:text-white"
              >
                <FileText size={16} /> View Full Résumé (PDF)
              </a>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <div
        className="mt-[38px] overflow-hidden rounded-[12px] border bg-white"
        style={{ borderColor: '#FCD34D' }}
        data-pdf-exclude
      >
        <div
          className="flex items-center gap-[9px] px-[22px] py-[13px]"
          style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}
        >
          <Lock size={15} style={{ color: '#B45309' }} />
          <strong className="text-[15px] font-semibold" style={{ color: '#7B3306' }}>
            Intro Notes
          </strong>
          <span
            className="rounded-[5px] px-[8px] py-[2px] text-[11.5px] font-semibold"
            style={{ background: '#FEE685', color: '#973C00' }}
          >
            Internal Only
          </span>
          <button
            type="button"
            onClick={() => setIntroNotesVisible((v) => !v)}
            className="ml-auto flex cursor-pointer"
            style={{ color: '#E17100' }}
            aria-label={introNotesVisible ? 'Hide internal notes' : 'Show internal notes'}
          >
            {introNotesVisible ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </div>
        {introNotesVisible ? (
          <ul className="flex flex-col gap-[11px] px-[24px] py-[18px]">
            {MOCK.introNotes.map((t, i) => (
              <li key={i} className="flex gap-[11px] text-[14px] leading-[1.55] text-[#29446D]">
                <span className="mt-[8px] h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: '#E8A23D' }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-[24px] py-[16px] text-[13px] italic text-[#60667C]">
            Internal notes hidden — click the eye to reveal.
          </div>
        )}
      </div>

      <div className="mt-6" data-pdf-exclude>
        <SaveBar
          showSave
          showSendEmail
          showRegenerate
          lastEditedDate={lastEdited.date || undefined}
          lastEditedTime={lastEdited.time}
          createdBy={lastEdited.by}
          onSave={handleSave}
          onSendEmail={handleSendEmail}
          onRegenerate={handleRegenerate}
          onPublish={handlePublish}
          regenerateDisabled={regenerating}
          publishDisabled={status === 'published'}
          publishLabel={status === 'published' ? 'Published' : 'Publish'}
        />
      </div>

      {toastMsg && <Toast message={toastMsg} onDismiss={() => setToastMsg(null)} />}
    </div>
  );
}
